<template>
  <div class="w-full mb-8">
    <!-- Barra de progresso visual -->
    <div class="relative">
      <div class="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-gray-200">
        <div
          :style="{ width: progressPercentage + '%' }"
          class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
        ></div>
      </div>

      <!-- Indicadores de steps -->
      <div class="flex justify-between items-center gap-2 w-full">
        <!-- Steps visíveis -->
        <div
          v-for="step in visibleSteps"
          :key="step.id || step.originalIndex"
          class="flex flex-col items-center flex-1 min-w-0"
        >
          <!-- Círculo do step -->
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300"
            :class="getStepClass(step.originalIndex + 1)"
          >
            <!-- Ícone de check se completado -->
            <svg
              v-if="step.originalIndex + 1 < currentStep"
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <!-- Número do step -->
            <span v-else>{{ step.originalIndex + 1 }}</span>
          </div>

          <!-- Label do step -->
          <span
            v-if="step.name"
            class="text-xs mt-2 text-center w-full truncate px-1"
            :class="{
              'text-blue-600 font-medium': step.originalIndex + 1 === currentStep,
              'text-gray-600': step.originalIndex + 1 !== currentStep
            }"
            :title="step.name"
          >
            {{ step.name }}
          </span>
        </div>

        <!-- Indicador de mais steps (+N) -->
        <div
          v-if="showMoreIndicator"
          class="flex flex-col items-center flex-1 min-w-0"
        >
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium bg-gray-300 text-gray-600"
            :title="`Mais ${remainingSteps} ${remainingSteps === 1 ? 'passo' : 'passos'}`"
          >
            <span>+{{ remainingSteps }}</span>
          </div>
          <span class="text-xs mt-2 text-center text-gray-600 w-full truncate px-1">
            Mais
          </span>
        </div>
      </div>
    </div>

    <!-- Texto do step atual -->
    <div v-if="showStepText" class="text-center mt-4">
      <p class="text-sm text-gray-600">
        Passo <span class="font-semibold">{{ currentStep }}</span> de
        <span class="font-semibold">{{ totalSteps }}</span>
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Garante que o container não ultrapasse a largura disponível */
.w-full {
  max-width: 100%;
}

/* Evita que os steps encavalhem */
.flex.justify-between.items-center {
  flex-wrap: nowrap;
  overflow: hidden;
}

/* Garante que cada step não quebre o layout */
.flex-1.min-w-0 {
  flex-shrink: 1;
  min-width: 0;
}

/* Trunca texto longo com ellipsis */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<script>
import { computed } from 'vue';

export default {
  name: 'StepNavigation',
  props: {
    steps: {
      type: Array,
      required: true
    },
    currentStep: {
      type: Number,
      required: true
    },
    totalSteps: {
      type: Number,
      required: true
    },
    showStepText: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const MAX_VISIBLE_STEPS = 5; // Máximo de steps visíveis antes de mostrar "+N"

    const progressPercentage = computed(() => {
      return (props.currentStep / props.totalSteps) * 100;
    });

    const getStepClass = (stepNumber) => {
      if (stepNumber < props.currentStep) {
        return 'bg-green-500 text-white';
      } else if (stepNumber === props.currentStep) {
        return 'bg-blue-500 text-white';
      } else {
        return 'bg-gray-300 text-gray-600';
      }
    };

    // Mostra indicador "+N" quando tem mais steps que o máximo
    const showMoreIndicator = computed(() => {
      return props.steps.length > MAX_VISIBLE_STEPS;
    });

    // Steps visíveis (ajusta dinamicamente baseado no step atual)
    const visibleSteps = computed(() => {
      const totalSteps = props.steps.length;

      // Se couber todos, mostra todos
      if (totalSteps <= MAX_VISIBLE_STEPS) {
        return props.steps.map((step, index) => ({
          ...step,
          originalIndex: index
        }));
      }

      // Tem mais steps que o máximo - precisa filtrar
      const currentIndex = props.currentStep - 1;
      const slotsForSteps = MAX_VISIBLE_STEPS - 1; // Reserva 1 slot para o indicador "+N"

      let start, end;

      // Lógica de janela deslizante centralizada no step atual
      const halfWindow = Math.floor(slotsForSteps / 2);

      // Tenta centralizar o step atual
      start = Math.max(0, currentIndex - halfWindow);
      end = start + slotsForSteps;

      // Ajusta se chegou no final
      if (end > totalSteps) {
        end = totalSteps;
        start = Math.max(0, end - slotsForSteps);
      }

      // Mapeia os steps visíveis com seus índices originais
      return props.steps.slice(start, end).map((step, relativeIndex) => ({
        ...step,
        originalIndex: start + relativeIndex
      }));
    });

    // Calcula quantos steps estão ocultos
    const remainingSteps = computed(() => {
      if (!showMoreIndicator.value) return 0;

      const totalSteps = props.steps.length;
      const visibleCount = visibleSteps.value.length;

      return totalSteps - visibleCount;
    });

    return {
      progressPercentage,
      getStepClass,
      visibleSteps,
      showMoreIndicator,
      remainingSteps
    };
  }
};
</script>
